import React__default, { forwardRef } from 'react';
import styled, { css } from 'styled-components';
import { motion } from 'framer-motion';
import { useRefObjectAsForwardedRef } from '../../hooks/useRefObjectAsForwardedRef.js';
import Box from '../../components/Box/Box.js';
import { get } from '../../constants.js';
import sx from '../../sx.js';

/**
 * A component that renders a box with a caret indicator positioned vertically. Maintains awareness of the y-axis center of a given element in the corresponding pane of the rule builder layout.
 */
const BoxWithIndicator = /*#__PURE__*/forwardRef(({
  arrowSize = 'lg',
  caretTop,
  isArrowAnimated = true,
  hideArrow = false,
  side = 'left',
  testId,
  children,
  sx: sxProp
}, forwardedRef) => {
  const containerRef = React__default.useRef(null);
  useRefObjectAsForwardedRef(forwardedRef, containerRef);

  // Set the default calculatedCaretTop value to 14px for `lg` arrowSize
  let calculatedCaretTop = 14;

  // Override calculatedCaretTop value if caretTop prop is provided
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  if (caretTop !== undefined) {
    calculatedCaretTop = caretTop;
  } else {
    // For `md` arrowSize, set calculatedCaretTop to 12px
    if (arrowSize === 'md') {
      calculatedCaretTop = 12;
    }
    // For `sm` arrowSize, set calculatedCaretTop to 11px
    else if (arrowSize === 'sm') {
      calculatedCaretTop = 11;
    }
  }

  const arrowTestId = `${testId}-arrow`;
  const wrapperTestId = `${testId}-wrapper`;
  return /*#__PURE__*/React__default.createElement(Wrapper, {
    "data-testid": testId ? wrapperTestId : undefined,
    ref: containerRef,
    sx: sxProp
  }, hideArrow ? null : /*#__PURE__*/React__default.createElement(Arrow, {
    arrowSize: arrowSize,
    caretTop: calculatedCaretTop,
    side: side,
    initial: {
      opacity: 0
    } // Initial opacity value
    ,
    animate: {
      opacity: 1,
      top: calculatedCaretTop
    } // Opacity and top values to animate to
    ,
    transition: {
      ease: 'easeInOut',
      // Easing function
      duration: 0.107 // Duration in seconds
    } // Transition configuration
    ,
    "data-testid": testId ? arrowTestId : undefined
  }), children);
});
const Wrapper = styled(Box).withConfig({
  displayName: "BoxWithIndicator__Wrapper",
  componentId: "sc-4fry8c-0"
})(["--border-color:", ";--background-color:", ";position:relative;display:flex;width:100%;height:100%;pointer-events:none;background-color:var(--background-color);border:2px solid var(--border-color);min-height:464px;", ""], get('colors.border.default'), get('colors.elevation.surface.default.[default]'), sx);
const Arrow = styled(motion.div).withConfig({
  displayName: "BoxWithIndicator__Arrow",
  componentId: "sc-4fry8c-1"
})(["--border-color:", ";--background-color:", ";position:absolute;display:block;isolation:isolate;width:0;height:0;pointer-events:none;content:' ';background-color:var(--background-color);border:2px solid var(--border-color);border-radius:", ";width:", ";height:", ";transform:", ";", ";", ";", ";", ";", ";", ";&::before{content:' ';display:block;position:absolute;background-color:var(--background-color);user-select:none;width:", ";height:", ";top:", ";left:0px;}"], get('colors.border.default'), get('colors.elevation.surface.default.[default]'), p => p.arrowSize === 'sm' ? '2px' : p.arrowSize === 'md' ? '2px' : '3px', p => p.arrowSize === 'sm' ? '11px' : p.arrowSize === 'md' ? '24px' : '32px', p => p.arrowSize === 'sm' ? '11px' : p.arrowSize === 'md' ? '24px' : '32px', p => p.side === 'left' ? 'translateY(-50%) translateX(50%) rotate(45deg)' : 'translateY(-50%) translateX(50%) rotate(-135deg)', p => p.arrowSize === 'sm' && p.side === 'left' && css(["left:-12px;right:100%;"]), p => p.arrowSize === 'md' && p.side === 'left' && css(["left:-25px;right:100%;"]), p => p.arrowSize === 'lg' && p.side === 'left' && css(["left:-33px;right:100%;"]), p => p.arrowSize === 'sm' && p.side === 'right' && css(["right:-2px;"]), p => p.arrowSize === 'md' && p.side === 'right' && css(["right:-2px;"]), p => p.arrowSize === 'lg' && p.side === 'right' && css(["right:-2px;"]), p => p.arrowSize === 'sm' ? '14px' : p.arrowSize === 'md' ? '35px' : '35px', p => p.arrowSize === 'sm' ? '17px' : p.arrowSize === 'md' ? '34px' : '44px', p => p.arrowSize === 'md' ? '-14px' : p.arrowSize === 'lg' ? '-16px' : '-10px');
BoxWithIndicator.displayName = 'BoxWithIndicator';
var BoxWithIndicator$1 = BoxWithIndicator;

export { BoxWithIndicator$1 as default };
